import React, { useState, useEffect } from 'react';
import SubmitButton from '../../components/SubmitButton';
import { Order, OrderResponse } from '../../models/APIModels';
import Moment from 'moment';
import Label from '../../components/Label';
import { axiosKK } from '../../services/networkRequest';
import Modal from 'react-modal';
import { Formik } from 'formik';
import AddressLookup, {
  Address,
  validateAddress,
} from '../../components/AddressLookup';
import errorMessage from '../../services/errorMessage';
import { AxiosError } from 'axios';
import DayPickerInputValue from '../../components/edit/DayPickerInputValue';
import { useDateDisabler } from '../../utilities/DateDisabler';
import { isWithinWorkingDays } from '../../utilities/CollectionDateConstraints';

interface OrderModalProps {
  update: (updated: Order) => void;
  close: () => void;
  editMode: boolean;
  order: Order;
}

interface OrderEditDeliveryDetailsErrors {
  deliveryDate?: string;
  deliveryAddress?: string;
}

// Utility function to handle undefined values
const safeValue = (value: any, fallback: any = '') => {
  if (
    value === null ||
    value === undefined ||
    value === 'undefined' ||
    value === ''
  ) {
    return fallback;
  }
  return value;
};

const isValidValue = (value: any) => {
  return (
    value !== null &&
    value !== undefined &&
    value !== 'undefined' &&
    value !== ''
  );
};

export default function OrderEditDeliveryDetailsModal({
  editMode,
  order,
  close,
  update,
}: OrderModalProps): JSX.Element {
  const disableDelivery = order.statuses.reduce<boolean>((previous, status) => {
    return previous && status.statusId >= 5;
  }, false);

  const [deliveryAddress, setDeliveryAddress] = useState<Address>({});
  const [networkError, setNetworkError] = useState<string>();
  // Add date validation error state
  const [deliveryDateError, setDeliveryDateError] = useState<string | null>(
    null,
  );

  // Initialize delivery address from order if it exists
  useEffect(() => {
    if (isValidValue(order.deliveryAddress)) {
      // Try to parse existing address or set empty state
      setDeliveryAddress({});
    }
  }, [order.deliveryAddress]);

  const deliveryDateIsBeforeCutOff =
    isValidValue(order.deliveryDate) &&
    isWithinWorkingDays(order.deliveryDate!, new Date(), 3, null, null);

  const disabledDays = useDateDisabler(
    null,
    isValidValue(order.collectionDate)
      ? new Date(order.collectionDate)
      : new Date(),
  );

  const initialValues: Order = {
    ...order,
  };

  return (
    <Modal
      isOpen={editMode}
      onRequestClose={close}
      style={{
        content: {
          display: 'flex',
        },
      }}
      contentLabel="Edit Delivery Details"
    >
      <Formik
        initialValues={initialValues}
        validate={({
          deliveryAddress: deliveryAddressString,
          deliveryDate,
        }: Order) => {
          const errors: OrderEditDeliveryDetailsErrors = {};

          // Validate delivery date
          if (
            isValidValue(deliveryDate) &&
            isValidValue(order?.collectionDate) &&
            (Moment(deliveryDate).isBefore(order.collectionDate) ||
              Moment(deliveryDate).isSame(order.collectionDate, 'date'))
          ) {
            const collectionDateFormatted = isValidValue(order?.collectionDate)
              ? Moment(order.collectionDate).format('dddd, MMM Do YYYY')
              : '[Date not available]';

            errors.deliveryDate = `Delivery date must be after collection date on ${collectionDateFormatted}. 
                                Alternatively, edit your collection details first`;
          }

          // Validate delivery address
          const currentDeliveryAddress = safeValue(order?.deliveryAddress);
          const newDeliveryAddress = safeValue(deliveryAddressString);

          // Check if address has changed or is new
          const addressHasChanged =
            currentDeliveryAddress !== newDeliveryAddress;
          const isNewAddress = !isValidValue(order.deliveryAddress);

          if (addressHasChanged || isNewAddress) {
            // If we have a new address string but no valid address object, it's incomplete
            if (
              isValidValue(deliveryAddressString) &&
              !validateAddress(deliveryAddress)
            ) {
              errors.deliveryAddress = 'Delivery address is incomplete';
            }
            // If we don't have an address string at all
            else if (!isValidValue(deliveryAddressString)) {
              errors.deliveryAddress = 'Delivery address is required';
            }
          }

          return errors;
        }}
        onSubmit={(values: Order, { setSubmitting, resetForm }) => {
          setSubmitting(true);
          axiosKK
            .put<OrderResponse>('/order/update', {
              id: values.id,
              collectionAddress: values.collectionAddress,
              deliveryAddress: values.deliveryAddress,
              collectionDate: values.collectionDate,
              deliveryDate: values.deliveryDate,
              collectionCountry: values.collectionCountry,
              deliveryCountry: deliveryAddress.country,
              collectionPostcode: values.collectionPostcode,
              deliveryPostcode: deliveryAddress.postCode,
            })
            .then(({ data }) => {
              update(data.order);
              resetForm();
              close();
            })
            .catch((error) => {
              errorMessage(setNetworkError)(error);
            })
            .catch((error: Error | AxiosError) => {
              const jsonError = JSON.parse(JSON.stringify(error));

              if (jsonError.hasOwnProperty('response')) {
                console.error((error as AxiosError).response?.data);
              } else if (jsonError.hasOwnProperty('message')) {
                console.error(error.message);
              } else {
                console.error(error);
              }
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          isSubmitting,
          handleBlur,
          handleChange,
          setFieldValue,
          handleSubmit,
          errors,
          isValid,
          dirty,
          setFieldTouched,
        }) => {
          // Include date picker validation in form validity check
          const formIsValid = isValid && !deliveryDateError;

          return (
            <>
              <form
                onSubmit={handleSubmit}
                className="order-edit-form"
                name="order-edit-form"
              >
                <div className="modal-header">
                  <h3>Edit Delivery Details</h3>
                  <SubmitButton red={true} onClick={close}>
                    Close
                  </SubmitButton>
                </div>
                <div className="modal-content">
                  {deliveryDateIsBeforeCutOff ? (
                    <div className="error-block">
                      <p>
                        Can not change delivery details within 3 working days of
                        delivery date
                      </p>
                    </div>
                  ) : null}
                  <div className="order-edit-inputs">
                    <Label htmlFor="date">Delivery Date</Label>
                    <div className="current-info">
                      <div className="current-label">
                        <h6>Current</h6>
                      </div>
                      <div className="info">
                        <span>
                          {isValidValue(order.deliveryDate)
                            ? Moment(order.deliveryDate).format(
                                'dddd, MMM Do YYYY',
                              )
                            : 'Not Set'}
                        </span>
                      </div>
                    </div>
                    <div className="DO_NOT_REMOVE_THIS_DIV">
                      <div className="dateLookup">
                        <div className="title-and-input">
                          <div className="title">
                            <section>Delivery Date:</section>
                            <img alt="" src="/img/calendar-icon.png" />
                          </div>
                          <span className="error">{errors.deliveryDate}</span>
                          {deliveryDateIsBeforeCutOff ? (
                            <span className="error">Cannot edit date</span>
                          ) : (
                            <DayPickerInputValue
                              id="deliveryDate"
                              handleFormChange={(field, value) => {
                                setFieldValue(field, value, true);
                                setFieldTouched(field, true, false);
                              }}
                              value={
                                isValidValue(values.deliveryDate)
                                  ? values.deliveryDate
                                  : undefined
                              }
                              disabled={disableDelivery}
                              onChange={handleChange}
                              handleBlur={handleBlur}
                              disabledDays={disabledDays}
                              onValidationError={setDeliveryDateError} // Track validation errors
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="order-edit-inputs">
                    <Label htmlFor="date">Delivery Address</Label>
                    <div className="current-info">
                      <div className="current-label">
                        <h6>Current</h6>
                      </div>
                      <div className="info">
                        <span>
                          {safeValue(order.deliveryAddress, 'Not Set')}
                        </span>
                      </div>
                    </div>
                    <div className="stuff">
                      <span className="error">{errors.deliveryAddress}</span>
                      {deliveryDateIsBeforeCutOff ? (
                        <span className="error">Cannot edit address</span>
                      ) : (
                        <AddressLookup
                          name="deliveryAddress"
                          address={deliveryAddress}
                          setAddress={(address, addressString) => {
                            // Update both local state and form state
                            setDeliveryAddress(address);
                            setFieldValue(
                              'deliveryAddress',
                              addressString,
                              true,
                            );
                            setFieldTouched('deliveryAddress', true, false);
                          }}
                          defaultValue={safeValue(values.deliveryAddress, '')}
                          onlyUnitedKingdom
                        >
                          {order.storageRequired &&
                            deliveryAddress.postCode &&
                            isValidValue(order.collectionAddress) &&
                            !order.collectionAddress.includes(
                              deliveryAddress.postCode,
                            ) && (
                              <p>
                                There is a surcharge of £3.99 per box to deliver
                                to a different area.
                              </p>
                            )}
                        </AddressLookup>
                      )}
                    </div>
                  </div>
                </div>
                {networkError && <span className="error">{networkError}</span>}
                <SubmitButton
                  disabled={
                    deliveryDateIsBeforeCutOff || !formIsValid || !dirty
                  }
                  isSubmitting={isSubmitting}
                >
                  Update
                </SubmitButton>
              </form>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
}
